/**
 * Entity-phrase resolution: a carved-out noun phrase → one catalog entity, a
 * clarification, or an honest failure (plan §6.3).
 *
 * @remarks
 * A noun phrase is `[adjective]* (noun | alias | pronoun)` — the HEAD is the
 * phrase's tail, and everything before it is adjective material. Resolution:
 *
 *   1. **Longest head wins.** Catalog names may be multiword (`blast door`),
 *      so the longest phrase tail that matches any entity's noun/alias is THE
 *      head; shorter readings are not considered once a longer one matches.
 *      Catalog names are trimmed/lowercased at comparison, mirroring
 *      `compileLexicon`'s generous authored intake.
 *   2. **Adjectives filter and consume; conjunctions and possessives are
 *      transparent.** Every word before the head must appear in a
 *      candidate's adjective list or that candidate is eliminated; consumed
 *      adjectives surface as `qualifiers`. A coordinating `and` among them
 *      (D81, Calder's D1) is neither — `the pinned and faded roster` reads
 *      exactly as `the pinned faded roster` — and so is a possessive
 *      determiner (D205): `examine my cases` is `examine the cases`, because
 *      the catalog models no owner the word could select on, and a word the
 *      parser cannot weigh must not be a word it fails on. A phrase whose
 *      adjectives eliminate everyone honestly
 *      fails, quoting the WHOLE phrase — `take the black and white keycard`
 *      is `entity_not_found` naming all five words when no keycard is both
 *      (design discovery 4, M3; quote widened by D81).
 *   2a. **The page's own words are adjectives too (D205, Mike's ruling).**
 *      Only when the adjectives eliminate EVERYONE: a candidate survives if
 *      every leading word is in its adjective list OR in its own
 *      description (`descriptionWords`, folded by the caller as input is).
 *      The lane says "an old man stands under the breadfruit tree", and
 *      `examine the old man` is the player typing the game's words back —
 *      an author cannot be asked to declare every modifier the prose will
 *      ever use. Declared adjectives keep their rung: they select first and
 *      alone, so `the black keycard` never reaches for a description. Two
 *      candidates whose pages both say the word tie, and a tie asks (rule 3).
 *   2b. **A possessive is read as its owner (D216).** `the estate's papers`
 *      is matched as `the estate papers` when the typed form matches
 *      nothing — the page says "estate", and the owner selects nothing the
 *      catalog does not already know. Declared adjectives and page words
 *      both read it (see `qualifierFormsOf`).
 *   3. **Salience weights survivors; closeness asks.** Selection and the
 *      0.15-window clarification live in `rank/select-or-clarify`.
 *
 * A pronoun head defers to `resolve-pronoun` (plan §4); words before a
 * pronoun are qualifiers of the thing it turns out to mean, and are read
 * against that thing exactly as words before a noun are (D229, from D226's
 * play): "the red it" is the red thing, and `pet it` is not the thing at
 * all, because `pet` is no word of its page — so the bare-noun rung declines
 * and the three-letter verb is read as one, as it is before a noun. A thing
 * recency names that is not in the catalog cannot be inspected, and the
 * words ride along as they always did. A pack's own word comes first (D226,
 * E12): a catalog that names a thing by the pronoun's word —
 * Drumline's Sylvain answers to `him`, the POC's projected woman to `her` —
 * has said who the word means here, and that declaration outranks the
 * recency heuristic (`examine the compass`, then `give the compass to him`:
 * him is Sylvain, not the compass). Recency answers when nobody present
 * claims the word.
 *
 * @packageDocumentation
 */

import { type PronounCanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type PhraseToken } from '../grammar/match-clause-structure.js';
import { type ObjectPreference } from '../lexicon/lexicon-definition.js';
import { singularFormsOf } from '../normalize/plurals.js';
import { selectOrClarify } from '../rank/select-or-clarify.js';
import {
	type ClarificationCandidate,
	type EntityRef,
	type ParseCandidateEntity,
	type PronounResolution,
} from '../types.js';
import {
	filterByAdjectives,
	filterByDescription,
	findStrandedEntities,
	type HeadMatch,
	isClaimedThroughTransparentWords,
	matchLongestHead,
	TRANSPARENT_QUALIFIER_WORDS,
} from './match-head.js';
import { resolvePronoun } from './resolve-pronoun.js';

/** The default world beyond the catalog: one that claims no name (D227). */
const NO_CLAIMED_NAMES: ReadonlySet<string> = new Set();

/** The outcome of resolving one phrase (result-typed; every shape is data the outcome builder maps). */
export type EntityPhraseOutcome =
	| { readonly candidates: readonly ClarificationCandidate[]; readonly type: 'needs_clarification' }
	| { readonly resolution: ResolvedEntityPhrase; readonly type: 'resolved' }
	| { readonly type: 'not_found'; readonly unmatchedPhraseTokens: readonly PhraseToken[] };

/** One fully-resolved noun phrase, ready for command assembly. */
export interface ResolvedEntityPhrase {
	readonly entityRef: EntityRef;
	/**
	 * D217: present when the phrase named a SET — a plural naming two or more
	 * things (`examine open jars`), every member in catalog order. Then
	 * {@link ResolvedEntityPhrase.entityRef} is the first member and says
	 * nothing on its own: the command carries the set, and how a set is
	 * answered is the consumer's (the builder's policy).
	 */
	readonly setEntityRefs?: readonly EntityRef[];
	/**
	 * D226 (E20): the HEAD as the player typed it — the noun or alias that
	 * matched (`ruts`, `wheel ruts`), the pronoun, or the plural word. A
	 * refusal that names the thing by the player's own word rather than the
	 * engine's framing reads this: `use the chain on the ruts` is refused
	 * about "the ruts", not "the room itself".
	 */
	readonly headSurface: string;
	/** Present when the phrase head was a pronoun resolved against recency. */
	readonly pronounResolution?: PronounResolution;
	/** Adjectives consumed while resolving, in phrase order. */
	readonly qualifiers: readonly string[];
	/** True when the head matched an alias rather than a canonical noun — worth ×0.97 (plan §6.1). */
	readonly wasAliasMatched: boolean;
}

/**
 * Resolve one noun phrase against the per-parse entity catalog.
 *
 * @param phraseTokens - The phrase as carved out by the structure matcher (non-empty by its contract)
 * @param entities - What is currently referable (architecture.md §4.4)
 * @param recentEntityRefs - Most-recent-first refs for pronoun heads
 * @param objectPreference - The verb's side of the room (D134); `none` asks when a noun answers on both
 * @param claimedNames - D227: every multiword name the world claims, present or not — a phrase spelling one is never read as its shorter head
 * @returns The resolution, a clarification, or `not_found` naming what could not be placed
 * @throws Error on an empty phrase (programmer error — the structure matcher never emits empty phrases)
 */
export const resolveEntityPhrase = (
	phraseTokens: readonly PhraseToken[],
	entities: readonly ParseCandidateEntity[],
	recentEntityRefs: readonly EntityRef[],
	objectPreference: ObjectPreference = 'none',
	claimedNames?: ReadonlySet<string>,
): EntityPhraseOutcome => {
	const headToken = phraseTokens[phraseTokens.length - 1];

	if (headToken === undefined) {
		throw new Error('resolveEntityPhrase requires a non-empty phrase; the structure matcher never emits empty ones');
	}

	if (headToken.kind === 'pronoun') {
		return resolvePronounHeadedPhrase(headToken, phraseTokens, recentEntityRefs, entities, objectPreference);
	}

	const phraseWords = phraseTokens.map((phraseToken) => phraseToken.sourceText);
	const headMatch = matchLongestHead(phraseWords, entities);

	if (headMatch === undefined) {
		// D26: before failing, look for a resolvable name STRANDED mid-phrase by
		// trailing chatter ("the weather like") — and ASK, never guess. D80
		// amends the ask: only when there is a CHOICE. D79 made asks persistent,
		// visible, two-plus-candidate state, and a which-one with one candidate
		// is not a question — so the stranded SINGLETON falls through to the
		// honest not-found teach (abstention held: an action clause never
		// executes on a guessed reading; the D29 question rung, which routes its
		// singleton into an information intent, already draws this line).
		const interiorCandidates = findStrandedEntities(phraseWords, entities);

		if (interiorCandidates.length >= 2) {
			return { candidates: interiorCandidates, type: 'needs_clarification' };
		}

		return { type: 'not_found', unmatchedPhraseTokens: phraseTokens };
	}

	// D227: a phrase that spells a name some absent thing claims is that
	// thing's name, and is not read as its shorter head on whatever is here
	// (`take her coin` is not `take coin`). No stranded-name rescue either:
	// the phrase placed exactly the thing it named, and that thing is not here.
	if (isClaimedThroughTransparentWords(phraseWords, headMatch, claimedNames ?? NO_CLAIMED_NAMES)) {
		return { type: 'not_found', unmatchedPhraseTokens: phraseTokens };
	}

	// D81 (Calder's D1 quote ruling): a head that RESOLVED and modifiers
	// that did not still fail as the WHOLE phrase the player typed. Quoting
	// only the modifiers ("nothing answers to 'pinned and faded'") while the
	// roster stands in front of them is the one register failure that makes
	// the parser look like it is not listening — and it made the same failure
	// read two different ways depending on whether the noun matched.
	return placeAmongHeadMatch(headMatch, objectPreference) ?? { type: 'not_found', unmatchedPhraseTokens: phraseTokens };
};

/**
 * Place a phrase among the candidates its head matched: the adjectives
 * filter, then the page (D205), then salience selects or asks (rule 3).
 *
 * @param headMatch - The longest head's candidates and the words before it
 * @param objectPreference - The verb's side of the room (D134)
 * @returns The resolution or the which-one question, or `undefined` when the qualifiers eliminate everyone
 */
const placeAmongHeadMatch = (headMatch: HeadMatch, objectPreference: ObjectPreference): EntityPhraseOutcome | undefined => {
	const adjectiveSurvivors = filterByAdjectives(headMatch.candidates, headMatch.qualifierWords);
	// D205: the description rung sits BELOW the adjective rung — reached only
	// when no declared adjective placed anyone, so an authored `black` keeps
	// selecting the black keycard even if the white one's page mentions the
	// word.
	const survivors =
		adjectiveSurvivors.length > 0 ? adjectiveSurvivors : filterByDescription(headMatch.candidates, headMatch.qualifierWords);

	if (survivors.length === 0) {
		return undefined;
	}

	const selection = selectOrClarify(survivors, objectPreference);

	if (selection.type === 'needs_clarification') {
		return {
			candidates: selection.contenders.map((contender) => ({
				entityRef: contender.entity.entityRef,
				matchedTokens: [...contender.qualifiers, contender.matchedName],
			})),
			type: 'needs_clarification',
		};
	}

	return {
		resolution: {
			entityRef: selection.selected.entity.entityRef,
			headSurface: selection.selected.matchedName,
			qualifiers: selection.selected.qualifiers,
			wasAliasMatched: selection.selected.matchKind === 'alias',
		},
		type: 'resolved',
	};
};

/**
 * D217: read a phrase as a PLURAL — its last word as the plural of a name the
 * catalog holds in the singular — and resolve it to every thing it names.
 *
 * @remarks
 * Mike's ruling (2026-09-28): a plural names the set. `examine open jars`
 * with four open jars in the room means all four of them, and asking "which
 * one?" answers a question the player did not ask. How the set is then
 * ANSWERED — one line for the set, each member in turn, or each up to a
 * count — is the builder's, and the consumer's to apply; this only says who
 * the set is.
 *
 * It is the SECOND reading, never the first. A thing whose own name is the
 * plural keeps it — the jar house's shelves answer `jars`, because the
 * shelves are the sealed dozens — so the caller reaches here only when the
 * phrase, read as written, placed nobody. The rungs are the singular
 * reading's (a head, then declared adjectives, then the page), so a plural
 * is described by exactly the words its members would be one at a time;
 * among the survivors the best-placed stand, as they would in a
 * which-one question.
 *
 * @param phraseTokens - The phrase as carved out by the structure matcher
 * @param entities - What is currently referable
 * @returns Every member of the set (one or more) and the qualifiers consumed, or `undefined` when the plural reading places nobody
 */
export const resolvePluralPhrase = (
	phraseTokens: readonly PhraseToken[],
	entities: readonly ParseCandidateEntity[],
): { readonly entityRefs: readonly [EntityRef, ...EntityRef[]]; readonly qualifiers: readonly string[] } | undefined => {
	const phraseWords = phraseTokens.map((phraseToken) => phraseToken.sourceText);
	const leadingWords = phraseWords.slice(0, -1);

	for (const singularWord of singularFormsOf(phraseWords.slice(-1).join(''))) {
		const headMatch = matchLongestHead([...leadingWords, singularWord], entities);

		if (headMatch !== undefined) {
			const adjectiveSurvivors = filterByAdjectives(headMatch.candidates, headMatch.qualifierWords);
			const survivors =
				adjectiveSurvivors.length > 0 ? adjectiveSurvivors : filterByDescription(headMatch.candidates, headMatch.qualifierWords);
			const bestPlacement = Math.max(...survivors.map((survivor) => survivor.entity.salience));
			const [firstMember, ...otherMembers] = survivors.filter((survivor) => survivor.entity.salience === bestPlacement);

			if (firstMember !== undefined) {
				return {
					entityRefs: [firstMember.entity.entityRef, ...otherMembers.map((member) => member.entity.entityRef)],
					qualifiers: headMatch.qualifierWords,
				};
			}
		}
	}

	return undefined;
};


/**
 * Resolve a pronoun-headed phrase: as a word the catalog declares first
 * (D226, E12), then against recency (plan §4).
 *
 * @param headToken - The pronoun head
 * @param phraseTokens - The whole phrase (for ride-along qualifiers)
 * @param recentEntityRefs - Most-recent-first refs
 * @param entities - The per-parse catalog, for the declared-word reading
 * @param objectPreference - The verb's side of the room (D134), for the declared-word reading
 * @returns The resolution, or `not_found` naming the pronoun
 */
const resolvePronounHeadedPhrase = (
	headToken: PronounCanonicalToken,
	phraseTokens: readonly PhraseToken[],
	recentEntityRefs: readonly EntityRef[],
	entities: readonly ParseCandidateEntity[],
	objectPreference: ObjectPreference,
): EntityPhraseOutcome => {
	// D226 (E12): the pack's own word first — a thing the catalog names by
	// this very word (`him` among Sylvain's nouns) is who the word means
	// here, whatever was mentioned last.
	const declaredReading = resolvePronounAsDeclaredWord(phraseTokens, entities, objectPreference);

	if (declaredReading !== undefined) {
		return declaredReading;
	}

	const pronounOutcome = resolvePronoun(headToken, recentEntityRefs, entities);

	if (pronounOutcome.type === 'unresolved') {
		return { type: 'not_found', unmatchedPhraseTokens: [headToken]};
	}

	// D229: the words before the pronoun are read against the thing it means,
	// as they would be against a noun — a word its page does not say is not
	// a qualifier of it, and the phrase names nothing.
	const qualifiers = phraseTokens.slice(0, -1).map((phraseToken) => phraseToken.sourceText);

	if (!qualifiersFitReferent(qualifiers, headToken.sourceText, pronounOutcome.resolution.entityRef, entities)) {
		return { type: 'not_found', unmatchedPhraseTokens: phraseTokens };
	}

	return {
		resolution: {
			entityRef: pronounOutcome.resolution.entityRef,
			headSurface: headToken.sourceText,
			pronounResolution: pronounOutcome.resolution,
			qualifiers,
			wasAliasMatched: false,
		},
		type: 'resolved',
	};
};

/**
 * D229: do the words before a pronoun fit the thing it means? Read through
 * the rungs a noun's qualifiers climb — declared adjectives, then the page
 * (D205) — against that one thing. Transparent words (`my`, `the chain's
 * conjunctions`) are dropped as the head match drops them. A referent the
 * catalog does not hold cannot be read, so its words ride along unread,
 * as they did before; the consumer's "not here" is the honest answer there.
 *
 * @param qualifiers - The words before the pronoun, as typed
 * @param pronounWord - The pronoun as typed — the head the words qualify
 * @param referent - What the pronoun resolved to
 * @param entities - The per-parse catalog
 * @returns Whether the phrase may name the referent
 */
const qualifiersFitReferent = (
	qualifiers: readonly string[],
	pronounWord: string,
	referent: EntityRef,
	entities: readonly ParseCandidateEntity[],
): boolean => {
	const judgedWords = qualifiers.filter((word) => !TRANSPARENT_QUALIFIER_WORDS.has(word));
	const entity = entities.find((candidate) => candidate.entityRef === referent);

	if (judgedWords.length === 0 || entity === undefined) {
		return true;
	}

	const candidate = { entity, matchedName: pronounWord, matchKind: 'noun' as const, qualifiers: judgedWords, score: entity.salience };

	return filterByAdjectives([candidate], judgedWords).length > 0 || filterByDescription([candidate], judgedWords).length > 0;
};

/**
 * D226 (E12): a pronoun-headed phrase read as the words the catalog may
 * declare — `him` among Sylvain's nouns, `her` as the projected woman's
 * alias — through the same head match and selection every other phrase
 * gets. `undefined` when no entity answers to the words.
 *
 * @param phraseTokens - The whole phrase, pronoun head included
 * @param entities - The per-parse catalog
 * @param objectPreference - The verb's side of the room (D134)
 * @returns The resolution or a which-one question, or `undefined` when the words name nothing
 */
const resolvePronounAsDeclaredWord = (
	phraseTokens: readonly PhraseToken[],
	entities: readonly ParseCandidateEntity[],
	objectPreference: ObjectPreference,
): EntityPhraseOutcome | undefined => {
	const headMatch = matchLongestHead(
		phraseTokens.map((phraseToken) => phraseToken.sourceText),
		entities,
	);

	return headMatch === undefined ? undefined : placeAmongHeadMatch(headMatch, objectPreference);
};
